import { Injectable, InjectionToken } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { User } from '../core/models/user.model';

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

export interface IResponse {
    isOk: boolean;
    data?: User;
    message?: string;
}

/**
 * Injection token used by guards, interceptors, and components.
 * The concrete implementation (Entra or Salesforce) is provided at bootstrap.
 */
export const AUTH_SERVICE = new InjectionToken<AuthService>('AuthService');

@Injectable()
export abstract class AuthService {
    protected readonly currentUserSubject = new BehaviorSubject<User | null>(null);
    public currentUser$ = this.currentUserSubject.asObservable();

    protected readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

    protected readonly initializedSubject = new BehaviorSubject<boolean>(false);
    public initialized$ = this.initializedSubject.asObservable();

    abstract login(): void;
    abstract loginPopup(): Observable<unknown>;
    abstract logout(): void;
    abstract logoutPopup(): Observable<void>;
    abstract getAccessToken(scopes?: string[]): Observable<string>;
    abstract getAccessTokenWithRetry(scopes?: string[]): Observable<string>;

    public isAuthenticated(): boolean {
        return this.isAuthenticatedSubject.value;
    }

    public getCurrentUser(): User | null {
        return this.currentUserSubject.value;
    }

    public updateCurrentUser(user: User): void {
        this.currentUserSubject.next(user);
    }

    abstract checkAndRefreshToken(): Observable<boolean>;

    public hasRole(role: string): boolean {
        const user = this.getCurrentUser();
        return user?.roles?.includes(role) || false;
    }

    public hasAnyRole(roles: string[]): boolean {
        const user = this.getCurrentUser();
        return user?.roles?.some(role => roles.includes(role)) || false;
    }

    public setPostLoginRedirect(url: string): void {
        try {
            localStorage.setItem(POST_LOGIN_REDIRECT_KEY, url);
        } catch {
            // Ignore localStorage errors
        }
    }

    public consumePostLoginRedirect(): string | null {
        try {
            const url = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
            if (url) {
                localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
                return url;
            }
        } catch {
            // Ignore localStorage errors
        }
        return null;
    }
}
